import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { isEmail } from 'validator';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { window } from '../../../../../globals';
import { get } from '../../../../../redux/api';
import * as ReportGenerationStore from '../../../../../redux/reportGeneration';
import { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, LabeledTextInput, LoaderButton } from '../../../../generic';
import Button from '../../../../generic/Button';
import useReportContainer from '../context/useReportContainer';
import { getEmailURL as getProjectEmailURL } from '../projectLevel/utils';
import { getEmailURL as getReleaseGroupEmailURL } from '../releaseGroupLevel/utils';
import type { ReportCategory, ReportExclusionOptions } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';

import styles from './EmailDialog.module.less';

type Props = {
  onClose: () => void;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
  excludeOptions?: ReportExclusionOptions;
};

/**
 * Dialog for customizing the recipients of an email report
 */
const EmailDialog: VoidFunctionComponent<Props> = ({
  onClose,
  includeOptions,
  dependencyMetadataOptions,
  reportType,
  format,
  authorName,
  authorEmail,
  supplier,
  excludeOptions,
}: Props) => {
  const { useReportsV2Toggle } = useReportContainer();
  const reportContext = useSelector(ReportGenerationStore.selectors.getReportContext);
  const addToast = useAddToast();
  const [emails, setEmails] = useState(window.user?.email || '');
  const [isLoading, setIsLoading] = useState(false);
  const areEmailsValid = emails.split(',').every((email) => isEmail(email.trim()));

  const onConfirm = async () => {
    const emailURL = reportContext.projectType === 'project' ? getProjectEmailURL({
      projectRevisionLocator: reportContext.revision,
      reportType,
      format,
      includeOptions,
      dependencyMetadataOptions,
      emails,
      authorName,
      authorEmail,
      supplier,
      excludeOptions,
      useReportsV2: useReportsV2Toggle,
    }) : getReleaseGroupEmailURL({
      groupId: reportContext.groupId,
      releaseId: reportContext.releaseId,
      reportType,
      format,
      includeOptions,
      dependencyMetadataOptions,
      excludeOptions,
      emails,
      authorName,
      authorEmail,
      supplier,
      useReportsV2: useReportsV2Toggle,
    });
    // Should never happen, but the type allows for null urls to handle the incorrect report type
    if (!emailURL) {
      onClose();
      return;
    }
    setIsLoading(true);
    try {
      await get(emailURL);
      addToast({
        type: 'success',
        content: 'Your email request is being processed. You will receive an email shortly.',
      });
    } catch (error) {
      addToast({
        type: 'error',
        content: 'Failed to send email',
      });
    }
    setIsLoading(false);
    onClose();
  };

  return (
    <Modal onHide={onClose} className="modal-confirm" show>
      <Modal.Header closeButton>
        <Modal.Title>Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.dialogBody}>
          <LabeledTextInput
            size="small"
            label="Email addresses"
            value={emails}
            onChange={setEmails}
          />
          <div className={styles.description}>Separate addresses with a comma</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.dialogFooter}>
          {!areEmailsValid && <div className={styles.error}>Please enter valid email addresses</div>}
          <ButtonGroup>
            <Button type="secondary" size={BUTTON_SIZE.MEDIUM} onClick={onClose}>
              Cancel
            </Button>
            <LoaderButton
              isLoading={isLoading}
              isDisabled={!areEmailsValid}
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onConfirm}
            >
              Email
            </LoaderButton>
          </ButtonGroup>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailDialog;
